import { createContext, useContext, useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase/firebaseConfig";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      // Store session time only for non-anonymous authenticated users
      if (firebaseUser && !firebaseUser.isAnonymous) {
        localStorage.setItem("loginTime", Date.now().toString());
      } else if (!firebaseUser) {
        localStorage.removeItem("loginTime");
      }

      // If user is anonymous, skip Firestore lookup and avoid noisy warnings
      if (firebaseUser && firebaseUser.isAnonymous) {
        setCurrentUserData(null);
        setLoading(false);
        return;
      }

      if (firebaseUser) {
        try {
          const userRef = doc(db, "usuarios", firebaseUser.uid);
          const docSnap = await getDoc(userRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            // User document found: keep it in context
            setCurrentUserData(data);
          } else {
            // Authenticated (non-anonymous) user without profile doc
            console.warn("⚠️ Usuario autenticado pero no existe en la colección 'usuarios'");
            setCurrentUserData(null);
          }
        } catch (error) {
          console.error("❌ Error al obtener datos del usuario:", error);
          setCurrentUserData(null);
        }
      } else {
        setCurrentUserData(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Define session TTL duration (1 hour)
  const SESSION_DURATION_MS = 60 * 60 * 1000;

  // Watch for TTL expiration
  useEffect(() => {
    const interval = setInterval(() => {
      const loginTime = localStorage.getItem("loginTime");
      const now = Date.now();

      if (loginTime && now - parseInt(loginTime, 10) > SESSION_DURATION_MS) {
        console.warn("🔒 Sesión expirada. Cerrando sesión.");
        auth.signOut();
        localStorage.removeItem("loginTime");
        setUser(null);
        setCurrentUserData(null);
      }
    }, 60000); // Check every 1 minute

    return () => clearInterval(interval);
  }, []);

  const rol = currentUserData?.rol || null;

  return (
    <AuthContext.Provider value={{ user, currentUserData, rol, loading }}>
      {children}
    </AuthContext.Provider>
  );
}
